import { CaretDown, CaretUp } from "@phosphor-icons/react";
import { PhosphorIcon, Price, Stack, SystemProps, Text } from "flicket-ui";
import { ComponentProps, ReactNode } from "react";
import { css } from "styled-components";
import { useOrganization } from "~hooks";
import { AnimatePriceLoading } from "./AnimatePriceLoading";

export interface RenderFeeProps {
  price: number;
}

export type ListItemProps = {
  title: ReactNode;
  subtitle?: ReactNode;
  price?: number;
  isLoading?: boolean;
  variant?: ComponentProps<typeof Text>["variant"];
  priceVariant?: ComponentProps<typeof Text>["variant"];
  renderPrice?: (props: RenderFeeProps) => JSX.Element;
  onClick?: () => void;
  caretPosition?: "up" | "down" | undefined;
  loaderWidth?: number;
};

function DefaultRenderPrice({ price }: RenderFeeProps): JSX.Element {
  const { organization } = useOrganization();
  return <Price price={price} currency={organization.currency} />;
}

export const ListItem = ({
  title,
  subtitle,
  price,
  isLoading,
  renderPrice = (props: RenderFeeProps) => <DefaultRenderPrice {...props} />,
  variant = "regular",
  priceVariant = variant,
  onClick,
  caretPosition,
  loaderWidth,
  ...props
}: ListItemProps & Omit<SystemProps, "title">) => {
  return (
    <Stack
      direction={"horizontal"}
      justifyContent="space-between"
      alignItems="baseline"
      gap={2}
      flex={1}
      onClick={onClick}
      cursor={onClick ? "pointer" : "default"}
      css={css`
        -webkit-tap-highlight-color: transparent;
      `}
      {...props}
    >
      <Stack direction={"vertical"} display="flex">
        <Text variant={variant} as={Stack} alignItems="center">
          {title}
          {caretPosition === "down" && (
            <PhosphorIcon icon={CaretDown} boxWidth={24} />
          )}
          {caretPosition === "up" && (
            <PhosphorIcon icon={CaretUp} boxWidth={24} />
          )}
        </Text>
        {subtitle && (
          <Text variant="small">
            <AnimatePriceLoading isLoading={isLoading} loaderWidth="60%">
              {subtitle}
            </AnimatePriceLoading>
          </Text>
        )}
      </Stack>
      {price !== undefined && (
        <Text variant={priceVariant} flex={1} textAlign={"right"}>
          <AnimatePriceLoading isLoading={isLoading} loaderWidth={loaderWidth}>
            {renderPrice({ price })}
          </AnimatePriceLoading>
        </Text>
      )}
    </Stack>
  );
};
